import { useMemo } from 'react';

import {
  useRentalManagerUserAnalyticsPropertiesWebApiHandler_getCompsForListing,
  useRentalManagerUserAnalyticsPropertiesWebApiHandler_getListingRanking,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type {
  GetUserPropertyDetailsResponseWebDto,
  ListingAlias,
  ListingCompDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const useCompetitiveInsights = (listingDetails?: GetUserPropertyDetailsResponseWebDto) => {
  const customerZrnNumber = listingDetails?.adproduct?.customerZrnNumber;
  const customerZrn = customerZrnNumber ? `ZRN-${customerZrnNumber}` : '';
  const propertyZrn = listingDetails?.adproduct?.zrnNumber ?? '';
  const zrnNumber = customerZrn && propertyZrn ? `${customerZrn}-${propertyZrn}` : '';
  const listingAlias = listingDetails?.listingAlias ?? ('' as ListingAlias);

  const { data: compsData, isPending: isCompsPending } =
    useRentalManagerUserAnalyticsPropertiesWebApiHandler_getCompsForListing(
      { zrnNumber, listingAlias, radius: 5 },
      {
        enabled: Boolean(zrnNumber),
        meta: {
          tags: ['competitiveInsightsComps'],
          context: { api: 'getCompsForListing', details: { zrnNumber } },
        },
      },
    );

  const { topOverlapComps, userComp } = useMemo(() => {
    if (!compsData) return { topOverlapComps: [] as Array<ListingCompDto>, userComp: undefined };

    const ownedComp = compsData.comps.find((c) => c.listingAlias === listingAlias)!;
    const others = compsData.comps.filter((c) => c.listingAlias !== listingAlias);
    const withOverlap = others
      .filter((c) => c?.overlapPerc > 0)
      .sort((a, b) => b.overlapPerc - a.overlapPerc)
      .slice(0, 4);
    let top: Array<ListingCompDto> = [];
    if (withOverlap.length > 0) {
      top = withOverlap;
    } else {
      const fallbckComps = others.slice().filter((comp) => !!comp.currentProductType);
      fallbckComps.sort((a, b) => a.distanceMiles - b.distanceMiles).slice(0, 4);
    }
    top.push(ownedComp);
    return { topOverlapComps: top, userComp: ownedComp };
  }, [compsData, listingAlias]);

  const aliasesToRank = useMemo(() => topOverlapComps.map((c) => c.listingAlias), [topOverlapComps]);

  const { data: rankingData, isPending: isRankingPending } =
    useRentalManagerUserAnalyticsPropertiesWebApiHandler_getListingRanking(
      { listingAlias, listingAliasesToRank: aliasesToRank, zrnNumber },
      {
        enabled: Boolean(listingAlias && aliasesToRank.length > 0 && !isCompsPending),
        meta: {
          tags: ['competitiveInsightsRanking'],
          context: { api: 'getListingRanking', details: { listingAlias } },
        },
      },
    );

  return {
    comps: compsData?.comps ?? [],
    overlapComps: compsData?.overlapComps ?? [],
    topOverlapComps,
    userComp,
    rankings: rankingData?.rankings ?? [],
    isLoading: isCompsPending || (aliasesToRank.length > 0 && isRankingPending),
  };
};
